/**
 * DOCUMENTATION:
 * Component: GCPOperations
 * Purpose: Deep dive into Cloud Monitoring, Logging, Trace, and SRE Principles.
 * * * Note for Junior Developers:
 * - Monitoring: 'What' is happening (CPU, Memory, Uptime).
 * - Logging: 'Why' it is happening (The detailed error messages).
 * - SRE: The bridge between Development and Operations.
 * - Error Budgets: The amount of downtime you are "allowed" to have to maintain your SLO.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Paper, LinearProgress, Divider
} from "@mui/material";

/* Icons */
import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import SearchIcon from '@mui/icons-material/Search';
import TimerIcon from '@mui/icons-material/Timer';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import VerifiedIcon from '@mui/icons-material/Verified';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

const SREBox = styled(Box)(({ theme, color }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    borderLeft: `4px solid ${color}`,
    borderRadius: "4px",
    height: "100%",
}));

export default function GCPOperations() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    CLOUD OPERATIONS & SRE
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>Reliability is the Most Important Feature</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. The Operations Suite */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <AssessmentIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Cloud Operations Suite</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.8 }}>
                            Google provides a unified toolset to observe your systems. You need to know which tool solves which problem.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={3}>
                                <SREBox color="#4285F4">
                                    <Typography variant="subtitle2" color="#4285F4">Monitoring</Typography>
                                    <Typography variant="caption">Dashboards and Alerts. Measures <strong>Metrics</strong> like CPU and Request latency.</Typography>
                                </SREBox>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <SREBox color="#34A853">
                                    <Typography variant="subtitle2" color="#34A853">Logging</Typography>
                                    <Typography variant="caption">Stores and searches application <strong>Logs</strong>. Can create metrics from logs.</Typography>
                                </SREBox>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <SREBox color="#FBBC04">
                                    <Typography variant="subtitle2" color="#FBBC04">Error Reporting</Typography>
                                    <Typography variant="caption">Groups and deduplicates <strong>Exceptions</strong> into a single dashboard.</Typography>
                                </SREBox>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <SREBox color="#EA4335">
                                    <Typography variant="subtitle2" color="#EA4335">Cloud Trace</Typography>
                                    <Typography variant="caption">Latency analysis. Shows exactly where a request is getting stuck in <strong>Microservices</strong>.</Typography>
                                </SREBox>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. SRE Fundamentals (SLI, SLO, SLA) */}
                <Grid item xs={12} md={8}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <QueryStatsIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>SRE Principles</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 4, opacity: 0.9 }}>
                            The exam will ask you to balance <strong>Reliability</strong> with <strong>Velocity</strong>.
                        </Typography>



                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="subtitle2" color="#4285F4">SLI (Indicator)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>A specific metric we measure. <em>e.g., "HTTP 200 Success Rate."</em></Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="#34A853">SLO (Objective)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>Our target goal. <em>e.g., "99.9% of requests must be HTTP 200."</em></Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="#EA4335">Error Budget</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>The gap between 100% and your SLO. <em>(0.1% downtime)</em>. If you spend it, deployments must stop until reliability is restored.</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* 3. Monitoring Health */}
                <Grid item xs={12} md={4}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <VerifiedIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>Uptime Checks</Typography>
                        </Stack>

                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                            Monitoring can perform **Black-box** testing using Uptime Checks from multiple regions globally.
                        </Typography>



                        <Stack spacing={2} mt={2}>
                            <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                                <Typography variant="subtitle2">Alerting Policies</Typography>
                                <Typography variant="caption">Notification channels: PagerDuty, Slack, Email, Pub/Sub, Webhooks.</Typography>
                            </Paper>
                            <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                                <Typography variant="subtitle2">Log-based Metrics</Typography>
                                <Typography variant="caption">Counting how many times a "Payment Failed" string appears in logs to create a graph.</Typography>
                            </Paper>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* PCA Exam Scenario */}
                <Grid item xs={12}>
                    <Alert severity="error" sx={{ bgcolor: "rgba(234, 67, 53, 0.1)", color: "white", border: "1px solid #EA4335" }}>
                        <strong>PCA ARCHITECT SCENARIO:</strong> "Your application is experiencing intermittent latency. The logs show everything is fine, but users in Europe are complaining about slow load times."
                        <br/><br/>
                        <strong>Correct Strategy:</strong> Use <strong>Cloud Trace</strong> to inspect the waterfall of requests. It will show if a specific cross-region database call is the bottleneck. Combine this with <strong>Cloud Profiler</strong> to see if a specific function in your code is consuming excessive CPU.
                    </Alert>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
